import React, { useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import { useT } from '../i18n/LanguageContext';

const VIDEO_URL =
  'https://customer-assets-0z36b82j.emergentagent.net/job_unruffled-cray-11/artifacts/kyzgdx3u_copy_4DC14CDB-EEE5-4678-B25D-E67F73A4BFBF.MOV';

// Poster from an existing illustration/photo we already use — mint themed
const POSTER =
  'https://images.unsplash.com/photo-1740657254989-42fe9c3b8cce?w=1600&q=70';

export default function VideoSection() {
  const { lang } = useT();
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);

  const heading1 = lang === 'pt' ? 'Veja a GiCleanly' : 'Watch GiCleanly';
  const heading2 = lang === 'pt' ? 'em ação' : 'in action';
  const kicker = lang === 'pt' ? 'Assista Agora' : 'Watch Now';
  const sub =
    lang === 'pt'
      ? 'Um vídeo rápido mostrando como cuidamos do seu espaço — do começo ao brilho final.'
      : 'A quick look at how we care for your space — from start to sparkling finish.';
  const tagline =
    lang === 'pt'
      ? 'Limpeza profissional, resultados que brilham.'
      : 'Professional cleaning, sparkling results.';

  const start = async () => {
    setStarted(true);
    // wait for element to mount
    setTimeout(async () => {
      try {
        if (videoRef.current) {
          await videoRef.current.play();
          setPlaying(true);
        }
      } catch (e) {
        // autoplay blocked — user will use controls
      }
    }, 60);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const goFullscreen = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitEnterFullscreen) el.webkitEnterFullscreen();
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <p className="uc-kicker mb-3">{kicker}</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1a2851]">
            {heading1} <span className="text-[#4dbfae]">{heading2}</span>
          </h2>
          <p className="text-[#5b6469] mt-4 max-w-2xl mx-auto">{sub}</p>
        </div>

        <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-[#1a2851] aspect-video group">
          {!started && (
            <>
              <img
                src={POSTER}
                alt="GiCleanly video"
                className="w-full h-full object-cover opacity-80 group-hover:opacity-95 transition-opacity"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a2851]/70 via-[#1a2851]/20 to-transparent" />
              <button
                onClick={start}
                aria-label="Play video"
                className="absolute inset-0 flex items-center justify-center"
              >
                <span className="relative">
                  <span className="absolute inset-0 rounded-full bg-[#4dbfae] animate-ping opacity-40" />
                  <span className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#4dbfae] hover:bg-[#3ba798] flex items-center justify-center shadow-2xl transition-colors">
                    <Play size={38} className="text-white ml-2" fill="white" />
                  </span>
                </span>
              </button>
              <div className="absolute bottom-6 left-6 md:bottom-8 md:left-8 text-white pointer-events-none">
                <p className="uc-kicker text-[#7fe0ce] mb-1">GiCleanly</p>
                <p className="font-serif text-xl md:text-2xl font-bold max-w-md">
                  {tagline}
                </p>
              </div>
            </>
          )}

          {started && (
            <>
              <video
                ref={videoRef}
                src={VIDEO_URL}
                className="absolute inset-0 w-full h-full object-cover bg-black"
                playsInline
                controls={false}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onClick={togglePlay}
              />

              {/* Custom controls overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#1a2851]/80 to-transparent flex items-center gap-3">
                <button
                  onClick={togglePlay}
                  aria-label={playing ? 'Pause' : 'Play'}
                  className="w-11 h-11 rounded-full bg-[#4dbfae] hover:bg-[#3ba798] text-white flex items-center justify-center transition-colors"
                >
                  {playing ? <Pause size={18} fill="white" /> : <Play size={18} fill="white" className="ml-0.5" />}
                </button>
                <button
                  onClick={toggleMute}
                  aria-label={muted ? 'Unmute' : 'Mute'}
                  className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                >
                  {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <div className="flex-1" />
                <button
                  onClick={goFullscreen}
                  aria-label="Fullscreen"
                  className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                >
                  <Maximize size={18} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
